/**
 * TaskCard Component
 * A compact task row with checkbox, name, assignee, due date, and status badge
 */

import { useState } from "react";
import { Avatar, Box, HStack, Icon, Tooltip, useToast } from "@chakra-ui/react";
import { useQueryClient } from "@tanstack/react-query";
import { LuCalendar } from "react-icons/lu";
import TaskCompleteToggleIcon from "features/Resources/Task/Components/Button/TaskCompleteToggleIcon";
import { isInPast } from "helpers/time";
import CDS from "design-system";
import { Task, TaskSummary } from "features/Resources/Task/models";
import { TaskStatus } from "features/Resources/Task/constants";
import { formatRelativeDueDate, isTaskClosed, showMarkCompleteButton, taskStartLabel } from "features/Resources/Task/helpers";
import { taskKeys, useUpdateTask } from "features/Resources/Task/hooks";
import TaskThreeDotOptionMenu from "features/Resources/Task/Components/Button/TaskThreeDotOptionMenu";
import { TaskActionRowButton } from "features/Resources/Task/Components/Action/Button";
import NewHireJourneyTag from "features/NewHireJourney/Components/NewHireJourneyTag";

interface TaskCardProps {
  task: Task;
  onClick?: (task: Task) => void;
  onOpenRemind?: () => void;
  showAssignee?: boolean;
  showJourney?: boolean;
  /** Compact list-row style with no card border */
  compact?: boolean;
}

// Grid column widths: checkbox | name (flex) | action btn | journey? | assignee? | status
const GRID_COL_CHECKBOX = "28px";
const GRID_COL_NAME = "1fr";
const GRID_COL_ACTION = "minmax(32px, auto)";
const GRID_COL_JOURNEY = "160px";
const GRID_COL_ASSIGNEE = "90px";
const GRID_COL_STATUS = "80px";

function buildCompactGridColumns({
  showJourney,
  showAssignee,
}: {
  showJourney: boolean;
  showAssignee: boolean;
}): string {
  const cols = [GRID_COL_CHECKBOX, GRID_COL_NAME, GRID_COL_ACTION];
  if (showJourney) cols.push(GRID_COL_JOURNEY);
  if (showAssignee) cols.push(GRID_COL_ASSIGNEE);
  cols.push(GRID_COL_STATUS);
  return cols.join(" ");
}

/** Header row for the compact list view — mirrors the grid columns of TaskCard compact mode */
export function TaskListHeader({
  showAssignee = false,
  showJourney = false,
}: {
  showAssignee?: boolean;
  showJourney?: boolean;
}) {
  const gridColumns = buildCompactGridColumns({ showJourney, showAssignee });

  const thStyle = {
    fontWeight: "600",
    color: "gray.500",
    textTransform: "uppercase" as const,
    letterSpacing: "wider",
  };

  return (
    <Box
      display="grid"
      gridTemplateColumns={gridColumns}
      alignItems="center"
      gap={1}
      px={6}
      py={3}
      borderBottom="1px solid"
      borderColor="gray.100"
    >
      <Box />
      <CDS.Text11 {...thStyle}>Name</CDS.Text11>
      <Box />
      {showJourney && <CDS.Text11 {...thStyle}>New Hire</CDS.Text11>}
      {showAssignee && <CDS.Text11 {...thStyle}>Assignee</CDS.Text11>}
      <CDS.Text11 {...thStyle} textAlign="right">
        Status
      </CDS.Text11>
    </Box>
  );
}

/** UI display states derived from task status and computed properties */
type TaskDisplayState =
  | "overdue"
  | "urgent"
  | "completed"
  | "skipped"
  | "closed_stale"
  | "not_started"
  | "blocked"
  | "active";

function getTaskDisplayState(task: TaskSummary): TaskDisplayState {
  if (task.status === TaskStatus.COMPLETED) return "completed";
  if (task.status === TaskStatus.SKIPPED) return "skipped";
  if (task.status === TaskStatus.CLOSED_STALE) return "closed_stale";
  if (task.status === TaskStatus.UPCOMING) return "not_started";
  if (task.isOverdue) return "overdue";
  if (task.isUrgent) return "urgent";
  if (task.status === TaskStatus.BLOCKED) return "blocked";
  return "active";
}

export function TaskStatusBadge({ task, size = "md" }: { task: TaskSummary; size?: "xs" | "sm" | "md" }) {
  const displayState = getTaskDisplayState(task);

  switch (displayState) {
    case "overdue":
      return <CDS.TaskStatusTag.OverdueBadge size={size} />;
    case "urgent":
      return <CDS.TaskStatusTag.UrgentBadge size={size} />;
    case "completed":
      return <CDS.TaskStatusTag.CompletedBadge size={size} />;
    case "skipped":
      return <CDS.TaskStatusTag.SkippedBadge size={size} />;
    case "closed_stale":
      return <CDS.TaskStatusTag.ClosedStaleBadge size={size} />;
    case "not_started":
      return <CDS.TaskStatusTag.NotStartedBadge size={size} />;
    case "blocked":
      return <CDS.TaskStatusTag.BlockedBadge size={size} />;
    case "active":
      return null;
    default:
      return null;
  }
}

export default function TaskCard({
  task,
  onClick,
  onOpenRemind,
  showAssignee = true,
  showJourney = false,
  compact = false,
}: TaskCardProps) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const { mutate: updateTask } = useUpdateTask(task?.id);

  const isCompleted = task.status === "completed";
  const isClosed = isTaskClosed(task);
  const isNotStarted = task.status === "upcoming";

  // Calculate opacity based on task state
  const getContentOpacity = () => {
    if (isClosed) return 0.6;
    if (isNotStarted) return 0.7;
    return 1;
  };

  // Optimistic local state for checkbox — includes tracked status to auto-clear when task status changes
  const [optimistic, setOptimistic] = useState<{ completed: boolean; forStatus: string } | null>(null);
  const displayCompleted = (optimistic?.forStatus === task.status ? optimistic.completed : null) ?? isCompleted;

  const handleToggleComplete = () => {
    const newCompleted = !displayCompleted;
    setOptimistic({ completed: newCompleted, forStatus: task.status });

    // Determine the status to set when un-completing
    const effectiveStartDate = task?.startedAt || task?.startAt;
    const taskIncompleteStatus = effectiveStartDate && isInPast(effectiveStartDate) ? "active" : "upcoming";
    const newStatus = newCompleted ? "completed" : taskIncompleteStatus;

    updateTask(
      { status: newStatus },
      {
        onSuccess: () => {
          // Invalidate queries to refresh data - optimistic state cleared by useEffect when task.status changes
          queryClient.invalidateQueries({ queryKey: taskKeys.byNewHireJourneys() });
          queryClient.invalidateQueries({ queryKey: taskKeys.lists() });
          queryClient.invalidateQueries({ queryKey: taskKeys.count() });
          queryClient.invalidateQueries({ queryKey: taskKeys.details() });
        },
        onError: () => {
          // Revert optimistic state on error
          setOptimistic(null);
          toast({
            title: "Failed to update task",
            status: "error",
            duration: 3000,
          });
        },
      },
    );
  };

  const handleClick = () => {
    onClick?.(task);
  };

  const formattedDueDate = formatRelativeDueDate(task.dueAt);

  const startLabel = taskStartLabel(task);

  if (compact) {
    const gridColumns = buildCompactGridColumns({
      showJourney: !!(showJourney && task.newHireJourneyId),
      showAssignee: !!showAssignee,
    });

    return (
      <Box
        display="grid"
        gridTemplateColumns={gridColumns}
        alignItems="center"
        gap={1}
        borderBottom="1px solid"
        borderColor="gray.100"
        px={2}
        py="6px"
        _hover={{ bg: "gray.50" }}
        cursor="pointer"
        onClick={handleClick}
      >
        {/* Checkbox */}
        <Box onClick={(e) => e.stopPropagation()} display="flex" alignItems="center">
          <TaskCompleteToggleIcon
            isCompleted={displayCompleted}
            isDisabled={!showMarkCompleteButton(task)}
            onClick={handleToggleComplete}
            size={4}
          />
        </Box>

        {/* Task name */}
        <CDS.Text14
          fontSize="13px"
          fontWeight="normal"
          color={isNotStarted ? "gray.500" : "gray.700"}
          textDecoration={isClosed ? "line-through" : "none"}
          noOfLines={1}
          minW={0}
          opacity={getContentOpacity()}
        >
          {task.name}
        </CDS.Text14>

        {/* Action button */}
        <Box onClick={(e) => e.stopPropagation()} display="flex" alignItems="center" justifyContent="center" pr={4}>
          <TaskActionRowButton task={task} />
        </Box>

        {/* Journey tag */}
        {showJourney && task.newHireJourneyId && (
          <Box onClick={(e) => e.stopPropagation()} overflow="hidden">
            <NewHireJourneyTag
              journeyId={task.newHireJourneyId}
              size="sm"
              linkTo={`/new-hires/${task.newHireJourneyId}/tasks`}
            />
          </Box>
        )}

        {/* Assignee */}
        {showAssignee &&
          (task.assignee ? (
            <Tooltip label={`Assignee: ${task.assignee.fullName}`} placement="top">
              <HStack spacing={1} justifyContent="flex-start">
                <Avatar
                  size="xs"
                  name={task.assignee.fullName}
                  src={task.assignee.avatarUrl}
                  boxSize="20px"
                  fontSize="8px"
                />
                <CDS.Text10 color="gray.500" noOfLines={1}>
                  {task.assignee.firstName}
                </CDS.Text10>
              </HStack>
            </Tooltip>
          ) : (
            <CDS.Text10 color="gray.400" fontStyle="italic" noOfLines={1}>
              Unassigned
            </CDS.Text10>
          ))}

        {/* Status */}
        <Box display="flex" justifyContent="flex-end">
          {isClosed ? (
            <TaskStatusBadge task={task} size="xs" />
          ) : isNotStarted ? (
            startLabel ? (
              <CDS.Text10
                color={startLabel.overdue ? "orange.500" : "gray.400"}
                fontStyle="italic"
                fontWeight={startLabel.overdue ? "500" : "normal"}
                whiteSpace="nowrap"
              >
                {startLabel.text}
              </CDS.Text10>
            ) : null
          ) : task.isOverdue ? (
            <CDS.Text10 color="red.500" fontWeight="500">
              Overdue
            </CDS.Text10>
          ) : formattedDueDate ? (
            <CDS.Text10 color="gray.400">
              {formattedDueDate}
            </CDS.Text10>
          ) : null}
        </Box>
      </Box>
    );
  }

  return (
    <Box
      bg="white"
      borderWidth="1px"
      borderColor="gray.200"
      borderRadius="md"
      px={3}
      py={2}
      _hover={{ bg: "gray.50" }}
      transition="all 0.15s"
      cursor="pointer"
      onClick={handleClick}
    >
      <HStack spacing={3} align="center">
        {/* Complete toggle */}
        <Box onClick={(e) => e.stopPropagation()} py={1} px={1}>
          <TaskCompleteToggleIcon
            isCompleted={displayCompleted}
            isDisabled={!showMarkCompleteButton(task)}
            onClick={handleToggleComplete}
          />
        </Box>

        {/* Task content - single line */}
        <HStack flex={1} minW={0} spacing={2} opacity={getContentOpacity()}>
          {/* Task name */}
          <Tooltip label="Not Started" isDisabled={!isNotStarted} placement="top">
            <Box flexShrink={1} minW={0}>
              <CDS.Text12
                fontWeight="medium"
                color={isNotStarted ? "gray.600" : "gray.800"}
                textDecoration={isClosed ? "line-through" : "none"}
                fontStyle={isNotStarted ? "italic" : "normal"}
                noOfLines={1}
              >
                {task.name}
              </CDS.Text12>
            </Box>
          </Tooltip>

          {/* Due date */}
          {formattedDueDate && (
            <HStack spacing={1} color="gray.500" fontSize="xs" flexShrink={0}>
              <Icon as={LuCalendar} boxSize={3} />
              <CDS.Text10>{formattedDueDate}</CDS.Text10>
            </HStack>
          )}

          {/* Status badge */}
          <Box flexShrink={0}>
            <TaskStatusBadge task={task} size="xs" />
          </Box>
        </HStack>

        {/* Right side: Action button, journey, avatar, and menu */}
        <HStack spacing={2} flexShrink={0} opacity={isClosed ? 0.6 : 1}>
          {/* Action button */}
          <Box onClick={(e) => e.stopPropagation()} mt="-2px">
            <TaskActionRowButton task={task} />
          </Box>

          {/* Journey tag */}
          {showJourney && task.newHireJourneyId && (
            <Box onClick={(e) => e.stopPropagation()} mt="4px">
              <NewHireJourneyTag
                journeyId={task.newHireJourneyId}
                size="sm"
                linkTo={`/new-hires/${task.newHireJourneyId}/tasks`}
              />
            </Box>
          )}

          {/* Divider between journey tag and assignee */}
          {showJourney && task.newHireJourneyId && showAssignee && task.assignee && (
            <CDS.Text10 color="gray.300">
              |
            </CDS.Text10>
          )}

          {/* Assignee avatar */}
          {showAssignee && task.assignee && (
            <Tooltip label={`Assignee: ${task.assignee.fullName}`} placement="top">
              <Avatar size="xs" name={task.assignee.fullName} src={task.assignee.avatarUrl} />
            </Tooltip>
          )}

          {/* Menu */}
          <Box onClick={(e) => e.stopPropagation()} py={1} px={1}>
            <TaskThreeDotOptionMenu task={task} onOpenRemind={onOpenRemind} exclude={["view_template"]} />
          </Box>
        </HStack>
      </HStack>
    </Box>
  );
}
